import { useState } from 'react'
import { XMarkIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'

export default function PDFViewer({ isOpen, onClose, pdfUrl, documentName = 'Document' }) {
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)

  if (!isOpen) return null

  const handleIframeLoad = () => {
    setIsLoading(false)
    setHasError(false)
  }

  const handleIframeError = () => {
    setIsLoading(false)
    setHasError(true)
  }

  const handleOpenInNewTab = () => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank', 'noopener,noreferrer')
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full h-full flex items-center justify-center p-4">
        <div className="bg-background rounded-lg shadow-xl w-full h-full max-w-6xl max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-gray-900 truncate">{documentName}</h2>
              {pdfUrl && (
                <button
                  onClick={handleOpenInNewTab}
                  className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-neutral-200-100 rounded-md transition-colors"
                  title="Open in new tab"
                >
                  <ArrowTopRightOnSquareIcon className="w-5 h-5" />
                </button>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-neutral-200-100 rounded-md transition-colors"
              title="Close"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 relative">
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-neutral-200-50">
                <div className="flex flex-col items-center gap-3">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary-600"></div>
                  <p className="text-gray-600">Loading document...</p>
                </div>
              </div>
            )}

            {hasError && (
              <div className="absolute inset-0 flex items-center justify-center bg-neutral-200-50">
                <div className="text-center max-w-md">
                  <div className="text-red-500 text-6xl mb-4">⚠️</div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Unable to load document
                  </h3>
                  <p className="text-gray-600 mb-4">
                    The document could not be displayed in the viewer. This might be due to browser
                    restrictions or file format issues.
                  </p>
                  <button
                    onClick={handleOpenInNewTab}
                    className="bg-secondary-600 hover:bg-orange-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                  >
                    Try opening in new tab
                  </button>
                </div>
              </div>
            )}

            {pdfUrl && (
              <iframe
                src={`${pdfUrl}#toolbar=1&navpanes=1&scrollbar=1`}
                className="w-full h-full border-0"
                title={documentName}
                onLoad={handleIframeLoad}
                onError={handleIframeError}
                style={{
                  display: hasError ? 'none' : 'block',
                  minHeight: '500px',
                }}
              />
            )}

            {!pdfUrl && (
              <div className="absolute inset-0 flex items-center justify-center bg-neutral-200-50">
                <div className="text-center">
                  <div className="text-gray-400 text-6xl mb-4">📄</div>
                  <p className="text-gray-600">No document URL provided</p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200 bg-neutral-200-50">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                <span>Viewing: {documentName}</span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleOpenInNewTab}
                  className="bg-neutral-200-600 hover:bg-neutral-200-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  Open in New Tab
                </button>
                <button
                  onClick={onClose}
                  className="bg-neutral-200 hover:bg-neutral-200 text-gray-800 px-4 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
